import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { NominationModules } from './constants';
import {
  AdmissionRequirement,
  ChiefSupervisor,
  Declaration,
  PAGE_TEXT,
  Remark,
  SupervisorTeam,
  NominationSidebar,
  Evaluation,
  type ScholarshipNomination,
  Fundings,
  panelOptions,
  type ScholarshipNominationMetadata,
  handleCheckFinishEvaluationQuestions,
  type FundingMappings,
  type FundingsValue,
  type ConfigurationMapping,
  EVALUATION_SCENE,
  type FundingSource,
} from './components';
import {
  useSubmitNomination,
  useGetNominationBatch,
  getApplicantInformation,
} from './graphql/NominationForm';
import { getOptionsValue } from '../Monitor/components';
import { useGetNominationInformation } from '../Assessment/graphql';
import {
  Alert,
  type AlertDetails,
  Dialog,
  type DialogDetails,
  Loading,
  NotificationText,
  UserRole,
} from '../../components';
import { useAuth } from '../../contexts';
import { APPLICATION_STATUS_TYPE, SimplifyRoles } from '../../utils';

export const Nomination: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });
  const { applicationId: applicationRoute, nominationId: id = '' } =
    useParams();
  const hasNominationId = Boolean(id); // true:  status >= under supervisor
  const hasApplicationId = Boolean(applicationRoute); // true:  status === submitted
  let batchSidebarDisabled = true;
  let applicationId = applicationRoute ?? '';
  let loading = false;
  let scholarshipNominationMetadatas: ScholarshipNominationMetadata = {
    academicExcellence: [],
    researchPotential: [],
    strengths: [],
    distinctiveQuality: [],
  };

  let scholarshipNominations: ScholarshipNomination = {
    polishStatus: null,
    academicProgramme: '',
    endorsement: false,
    questions: [],
    scores: [],
    justification: '',
    UPAFDimension: [],
    HKPFSDimension: [],
    candidatesDimension: [],
    sectionalScores: [],
    eligibilityCheck: [],
    interviewPanel: {
      panelAgreement: false,
      interviewLanguage: '',
      interviewMode: '',
      interviewDate: '',
      panelMembers: [],
    },
    matchedQuestionsData: {
      academicExcellenceQuestions: [],
      researchPotentialQuestions: [],
      strengthsQuestions: [],
      distinctiveQualityQuestions: [],
    },
  };
  let subjectClassification: string | undefined;
  let refetchNominationInformation = (): void => {};
  let competitiveStudentship: number | null = null;
  let isUPAF = false;

  const {
    admission,
    supervisorTeam,
    fundings,
    declarations,
    remarks,
    departmentOptions,
    hasSubmitted,
    loading: nominationBatchLoading,
    error: nominationBatchError,
    refetch: refetchNominationBatch,
  } = useGetNominationBatch(id); // this is nomination id

  // has been accepted
  if (!hasApplicationId) {
    batchSidebarDisabled = false;
    const {
      nominationInformation,
      scholarshipNomination,
      scholarshipNominationMetadata,
      isUPAF: upafFlag,
      loading: loadNominationInfo,
      error: errRequestNominationInfo,

      refetch,
    } = useGetNominationInformation(
      {
        _id: { eq: id }, // For the situation where only nomination id exists
      },
      hasApplicationId, // skip if has not been accepted
    );
    scholarshipNominationMetadatas = scholarshipNominationMetadata;
    // if is UPAF, evaluation is required.
    isUPAF = upafFlag;
    subjectClassification = nominationInformation?.panel ?? '';
    competitiveStudentship =
      nominationInformation?.competitiveStudentship ?? null;
    scholarshipNominations = scholarshipNomination;
    loading = loadNominationInfo;
    refetchNominationInformation = refetch;
    applicationId = nominationInformation?.applicationId ?? '';
    // if this nomination has been submitted, redirect to monitor

    if (errRequestNominationInfo)
      return (
        <Alert
          openAlert={true}
          setOpenAlert={() => {}}
          alertDetails={{
            alertContent:
              'Error Occurs.' + (errRequestNominationInfo as Error)?.message,
            alertType: 'error',
          }}
        />
      );
  }

  const sources = fundings?.map(funding => funding.source as FundingSource);

  const supervisors = supervisorTeam.map(supervisor => {
    const { confirmedAsCoSupervisor, ...rest } = supervisor;
    return {
      ...rest,
    };
  });

  const { programme: programmeInGsb } = admission;
  const countCoSup = supervisorTeam?.length - 1;
  const countCoSupConfirmed = supervisorTeam.filter(
    member => member.confirmedAsCoSupervisor === true,
  ).length;

  const {
    applicationInfoData,
    fundingSourceConfiguration,
    isFinishedResearchAssessment,
    loading: applicationInfoDataLoading,
    error: applicationInfoDataError,
  } = getApplicantInformation(applicationId);
  const { mapping: fundingMapping } = fundingSourceConfiguration;
  const {
    status,
    applicationNumber,
    proposedSupervisor,
    rejectedByFirstChoice,
    proposedChiefSupervisorBackup,
    remarks: returnedRemarks,
  } = applicationInfoData;

  const editableStatus = [
    APPLICATION_STATUS_TYPE.SUBMITTED,
    APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR,
  ];
  loading = loading || nominationBatchLoading || applicationInfoDataLoading;

  if (
    !loading &&
    !editableStatus.includes(status as APPLICATION_STATUS_TYPE) &&
    applicationId !== ''
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const submitNomination = useSubmitNomination();
  const hasPreviousSuccessfulSupervisor = supervisorTeam?.some(
    member => member.previousSuccessfulSupervision,
  );

  const handleChangeActivated = (activatedId: NominationModules): void => {
    setActivatedModuleId(activatedId);
  };

  const isPendingCoSup =
    status === APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR &&
    countCoSup !== countCoSupConfirmed &&
    hasSubmitted;

  // safety season.
  // in case supervisor enter delegation mode when nominating his/her own applicant
  const hasPermissionToNominate =
    (Boolean(proposedSupervisor) &&
      proposedSupervisor === user?.sub &&
      !rejectedByFirstChoice) ||
    (rejectedByFirstChoice && proposedChiefSupervisorBackup === user?.sub);

  const handleCombineRefetch = (): void => {
    refetchNominationBatch();
    refetchNominationInformation();
  };

  const handleCheckInvalidFundingOption = (
    fundingMapping: FundingMappings,
    fundings: FundingsValue[],
  ): { invalid: boolean; invalidReason: string } => {
    let invalidReason: string = '';

    const existDisableFunding = fundings.some(funding => {
      const { schema, source } = funding;
      if (schema === '') return true;
      // can not submit if theres disabled funding

      const fundingConfig = fundingMapping[schema].find(
        (funding: ConfigurationMapping) => funding.value === source,
      );

      invalidReason =
        fundingConfig?.disabledDesc ??
        PAGE_TEXT.VALIDATION_SUBMIT.CHANGE_FUNDING;
      return fundingConfig?.disabled ?? false;
    });

    return {
      invalid: existDisableFunding,
      invalidReason,
    };
  };

  const nominationModulesMap: Partial<
    Record<
      NominationModules,
      { component: React.ReactNode; moduleTitle: string; reachable: boolean }
    >
  > = {
    [NominationModules.CHIEF_SUPERVISOR]: {
      component: (
        <ChiefSupervisor
          applicationInfoData={applicationInfoData}
          onMoveNext={handleChangeActivated}
          refetchNominationInformation={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.CHIEF_SUPERVISOR.CHIEF_SUPERVISOR.TITLE,
      reachable: true,
    },
    [NominationModules.SUPERVISOR_TEAM]: {
      component: (
        // when isPendingCoSup===true, <SupervisorTeam /> can still be edited.
        <SupervisorTeam
          supervisorTeam={supervisors}
          isPendingCoSup={batchSidebarDisabled}
          refetchSupervisorTeam={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.SUPERVISOR_TEAM.TITLE,
      reachable: !batchSidebarDisabled,
    },
    [NominationModules.FORM]: {
      component: (
        <AdmissionRequirement
          admission={admission}
          isViewMode={batchSidebarDisabled}
          applicationId={applicationId}
          refetchAdmission={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.GSB1_FORM.TITLE,
      reachable: !batchSidebarDisabled,
    },
    [NominationModules.FUNDINGS]: {
      component: (
        <Fundings
          subjectClassification={getOptionsValue(
            panelOptions,
            subjectClassification ?? '',
          )}
          competitiveStudentship={competitiveStudentship}
          isViewMode={batchSidebarDisabled}
          applicationId={applicationId}
          applicationNo={applicationNumber}
          programmeInGsb={programmeInGsb}
          departmentOptions={departmentOptions}
          fundingMapping={fundingMapping}
          sources={sources}
          fundings={fundings}
          refetchFunding={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.FUNDING.TITLE,
      reachable: !batchSidebarDisabled,
    },

    [NominationModules.EVALUATION]: {
      component: (
        <Evaluation
          scene={EVALUATION_SCENE.NOMINATION}
          applicationId={applicationId}
          scholarshipNomination={scholarshipNominations}
          refetchEvaluation={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.EVALUATION.TITLE,
      reachable: !batchSidebarDisabled && isUPAF,
    },
    [NominationModules.DECLARATION]: {
      component: (
        <Declaration
          fundings={fundings}
          declarations={declarations}
          isViewMode={batchSidebarDisabled}
          refetchDeclarations={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.DECLARATION.TITLE,
      reachable: !batchSidebarDisabled,
    },
    [NominationModules.REMARKS]: {
      component: (
        <Remark
          remarks={remarks}
          isViewMode={batchSidebarDisabled}
          returnedRemarks={returnedRemarks}
          refetchRemark={handleCombineRefetch}
        />
      ),
      moduleTitle: PAGE_TEXT.REMARKS.TITLE,
      reachable: !batchSidebarDisabled,
    },
  };
  const hasCoSup = supervisorTeam.length > 1;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // am means Activated Module
  let sidebarActivated = searchParams.get('am') as NominationModules;

  if (
    !Object.values(NominationModules).includes(sidebarActivated) ||
    batchSidebarDisabled ||
    !nominationModulesMap[sidebarActivated]?.reachable
  ) {
    sidebarActivated = NominationModules.CHIEF_SUPERVISOR;
  }

  const [activatedModuleId, setActivatedModuleId] = useState(sidebarActivated);

  useEffect(() => {
    const keys = Array.from(searchParams.keys());
    // delete unnecessary param
    keys.forEach(key => {
      if (key !== 'am') {
        searchParams.delete(key);
      }
    });

    searchParams.set('am', activatedModuleId);

    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedModuleId]);

  const validationChecks = [
    {
      condition: !hasPreviousSuccessfulSupervisor,
      message: PAGE_TEXT.VALIDATION_SUBMIT.FILL_SUPERVISORTEAM,
      modules: NominationModules.SUPERVISOR_TEAM,
    },
    {
      condition:
        admission?.programme === '' || admission?.programme === undefined,
      message: PAGE_TEXT.VALIDATION_SUBMIT.FILL_GSB,
      modules: NominationModules.FORM,
    },
    {
      condition:
        (fundings ?? [])?.length === 0 ||
        fundings?.some(funding => !funding.formFinished),
      message: PAGE_TEXT.VALIDATION_SUBMIT.FILL_FUNDING,
      modules: NominationModules.FUNDINGS,
    },
    // [RPG-1583]
    // Block the Schemes for Supervisor Selection that are disabled and display the reason
    {
      condition: handleCheckInvalidFundingOption(fundingMapping, fundings ?? [])
        .invalid,
      message: handleCheckInvalidFundingOption(fundingMapping, fundings ?? [])
        .invalidReason,
      modules: NominationModules.FUNDINGS,
    },

    {
      condition:
        isUPAF &&
        (!handleCheckFinishEvaluationQuestions(
          scholarshipNominationMetadatas,
          scholarshipNominations.questions,
          SimplifyRoles.SUPERVISOR,
        ) ||
          !isFinishedResearchAssessment),
      message: PAGE_TEXT.VALIDATION_SUBMIT.FILL_EVALUATION,
      modules: NominationModules.EVALUATION,
    },
    {
      condition: declarations.length === 0,
      message: PAGE_TEXT.VALIDATION_SUBMIT.FILL_DECLARATION,
      modules: NominationModules.DECLARATION,
    },
  ];

  const confirmSubmitNomination = async (): Promise<void> => {
    setDialogOpen(false);
    // submit the nomination will change the application status to SUPERVISOR_NOMINATED
    try {
      const { data } = await submitNomination({
        variables: {
          id,
          input: {
            submitted: true,
          },
        },
      });
      if (!data?.updateNominationSubmissionStatus?.submitted) {
        setAlertDetails({
          alertContent: data?.updateNominationSubmissionStatus?.failureMessage,
          alertType: 'warning',
        });
        setOpenAlert(true);
        return;
      }

      setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
      setDialogOpen(true);
    } catch (e) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const handleBackTolist = (): void => {
    window.location.href = `/internal/nominations/application-list`;
  };
  const handleJumpViewRemarks = (): void => {
    setActivatedModuleId(NominationModules.REMARKS);
    setDialogOpen(false);
  };

  const handleSubmitNomination = async (): Promise<void> => {
    for (const check of validationChecks) {
      if (check.condition) {
        setAlertDetails({
          alertContent: check.message,
          alertType: 'warning',
        });
        setOpenAlert(true);
        setActivatedModuleId(check.modules);

        return;
      }
    }
    // pop up a dialog to confirm the submission
    setDialogDetails(dialogMappings[PAGE_TEXT.SUBMIT]);
    setDialogOpen(true);
  };

  if (nominationBatchError ?? applicationInfoDataError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            'Error Occurs.' +
            (nominationBatchError as Error)?.message +
            (applicationInfoDataError as Error)?.message,
          alertType: 'warning',
        }}
      />
    );
  }

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = nominationModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.SUBMIT]: {
      title: PAGE_TEXT.SUBMIT_CONFIRM.TITLE,
      content: hasCoSup
        ? PAGE_TEXT.SUBMIT_CONFIRM.CONTENT_CO_SUP
        : PAGE_TEXT.SUBMIT_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: confirmSubmitNomination,
    },

    // on submit successfully
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content: hasCoSup
        ? PAGE_TEXT.VALIDATION_SUBMIT.SUBMIT_SUCCESS_CO_SUP
        : PAGE_TEXT.VALIDATION_SUBMIT.SUBMIT_SUCCESS,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBackTolist,
    },
    // on open when it is a returned nomination
    [PAGE_TEXT.PREVIEW_DIALOG.TITLE]: {
      title: PAGE_TEXT.PREVIEW_DIALOG.TITLE,
      content: PAGE_TEXT.PREVIEW_DIALOG.CONTENT,
      confirmAction: PAGE_TEXT.OK,
      handleDialogConfirmAction: handleJumpViewRemarks,
    },
  };
  useEffect(() => {
    const supRelatedRemark = returnedRemarks.filter(
      rmk =>
        rmk.fromRole === UserRole.SUPERVISOR ||
        rmk.toRole.includes(UserRole.SUPERVISOR),
    );

    if (!loading && supRelatedRemark.length && !isPendingCoSup) {
      setDialogDetails(dialogMappings[PAGE_TEXT.PREVIEW_DIALOG.TITLE]);
      setDialogOpen(true);
    }
  }, [loading]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Dialog
        openDialog={dialogOpen}
        width={'700px'}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={handleCloseDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      <PageContainer>
        {hasPermissionToNominate ? (
          <>
            <div className="sidebar-area">
              <NominationSidebar
                sidebarDisabled={batchSidebarDisabled}
                onChangeActivated={handleChangeActivated}
                onSubmitNomination={handleSubmitNomination}
                hasNominationId={hasNominationId}
                currentActivatedModule={activatedModuleId}
                isUPAF={isUPAF}
              />
            </div>
            <Container>
              <h1>
                {PAGE_TEXT.TITLE} - {moduleTitle}
              </h1>

              <div className="form-area">
                {isPendingCoSup && (
                  <NotificationText note={PAGE_TEXT.PENDING_NOTE} />
                )}
                {moduleComponent}
              </div>
            </Container>
          </>
        ) : (
          <div className="p-8">
            <h2 className="font-bold">{PAGE_TEXT.ACCESS_DENIED.TITLE}</h2>
            <h2 className="mt-4">
              {PAGE_TEXT.ACCESS_DENIED.CONTENT_1}
              &nbsp;
              {'('}
              <span className="underline decoration-pink-500/30">
                {proposedSupervisor}
              </span>
              {')'}
              &nbsp;
              {PAGE_TEXT.ACCESS_DENIED.CONTENT_2}
            </h2>
            <div
              dangerouslySetInnerHTML={{
                __html: PAGE_TEXT.ACCESS_DENIED.BACK_HOME,
              }}
            />
          </div>
        )}
      </PageContainer>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};

const PageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;

  .sidebar-area {
    width: 18.5rem;
    border-right: 0.1rem solid #eee;
    padding-right: 0.1rem;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .form-area {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 100vh;
  }
`;
